import React, { createContext, useContext } from "react";
import {
  getData,
  patchData,
  postData,
  deleteData,
} from "../utilities/postData";
import { useDualMsal } from "./MsalContext";

const DataProviderContext = createContext();

export const useData = () => {
  return useContext(DataProviderContext);
};

export function DataProvider({ children }) {
  const { currentUser, getToken, isAuthenticated } = useDualMsal();
  const getUserState = async (userId) => {
    const token = await getToken();
    return getData({
      endpoint: "user" + (userId ? "/" + userId : ""),
      accessToken: token,
    });
  };

  // Identity list — staff and (optionally) participants from the `users`
  // container with no participation data overlaid. Use this for the Staff
  // tab and any UI that only needs name/email/role/site/lastLogin. For
  // participant rows that need assignments/relmedId/withdrawn/etc, use
  // `getParticipants` instead (hits /api/participants which merges in
  // participation state and fans out recurring sessions).
  const getUsersStatuses = async () => {
    const token = await getToken();
    return getData({
      endpoint: "users",
      accessToken: token,
    });
  };

  // Admin participant list. Returns one row per participant, identity
  // merged with participation. Recurring sessions are pre-fanned into
  // one row per materialised occurrence — what you get back here is
  // shape-compatible with the assignments inside /api/participant GET.
  // Site-scoped for admins (own site + TEST); PI sees all.
  const getParticipants = async () => {
    const token = await getToken();
    return getData({
      endpoint: "participants",
      accessToken: token,
    });
  };

  // Admin participant detail — full TaskStates-rehydrated row for the
  // config dialog. Reads the live document from Cosmos so admin edits
  // never round-trip through a stale list-view snapshot. Pass the
  // participant's b2c_id (their users.id); the backend resolves
  // `<b2c_id>_<study_id>` internally.
  const getParticipantById = async (participantId, { studyId } = {}) => {
    const token = await getToken();
    const study = studyId || "PRADAM";
    return getData({
      endpoint:
        "participant/" +
        encodeURIComponent(participantId) +
        "?study_id=" +
        encodeURIComponent(study),
      accessToken: token,
    });
  };

  const createInvite = async (data) => {
    const token = await getToken();
    return postData({
      timeout: 30000,
      endpoint: "create_invite",
      accessToken: token,
      data: data,
    });
  };

  const checkRelmedId = async (relmedId) => {
    const token = await getToken();
    return postData({
      timeout: 8000,
      endpoint: "check_relmed_id",
      accessToken: token,
      data: { relmedId },
    });
  };

  // Identity write — name, role, site, photoURL, contactIds, etc.
  // Hits /api/user, which writes to the users container only.
  // Strips participation fields from the payload as a safety net in case a
  // caller passes a whole user-shaped object including assignments — those
  // belong on /api/participant. The backend will 400 if they slip through,
  // so this is a courtesy not a guarantee.
  const PARTICIPATION_KEYS = [
    "assignments",
    "session_state",
    "lastDataSubmission",
    "drug",
    "pharmacy",
  ];

  const setUserState = async (u) => {
    const cleanedEntries = Object.entries(u).filter(
      ([key]) => !PARTICIPATION_KEYS.includes(key)
    );
    const cleaned = Object.fromEntries(cleanedEntries);
    const suffix = cleaned.id && cleaned.id !== currentUser?.userId ? "/" + cleaned.id : "";
    const token = await getToken();
    return patchData({
      endpoint: "user" + suffix,
      accessToken: token,
      data: cleaned,
    });
  };

  // Participation write — assignments, session_state, study-specific fields.
  // Hits /api/participant, which writes to the Participants container only.
  // For admin edits of another participant, pass the target's b2c_id as
  // `participantId`; for self-edits, omit it and the backend uses the JWT sub.
  const setParticipantConfig = async (data, { participantId, studyId } = {}) => {
    const token = await getToken();
    const suffix = participantId ? "/" + participantId : "";
    const study = studyId || "PRADAM";
    return patchData({
      endpoint: "participant" + suffix + "?study_id=" + encodeURIComponent(study),
      accessToken: token,
      data: { ...data, study_id: study },
    });
  };

  const postTaskData = async (data) => {
    const token = await getToken();
    // Add participantId to the call for caching purposes
    return postData({
      endpoint: "post-data",
      accessToken: token,
      participantId: currentUser?.userId, // Pass the current user's Zitadel sub as participantId
      data: data,
    });
  };

  const resendInvite = async (userId, isPasswordReset = false) => {
    const token = await getToken();
    return postData({
      endpoint: "resend_invite",
      accessToken: token,
      data: { userId, isPasswordReset },
    });
  };

  // Update getSubmissionMetrics to accept an optional site parameter
  const getSubmissionMetrics = async (site = null) => {
    const token = await getToken();
    const params = {};
    if (site && site !== "All Sites") {
      params.site = site; // Add site to parameters if it's not "All Sites"
    }
    return getData({
      endpoint: "submission-metrics",
      accessToken: token,
      parameters: params, // Pass parameters to getData
    });
  };

  const retrySubmission = async (relmedId) => {
    const token = await getToken();
    return postData({
      endpoint: "retry-submission",
      accessToken: token,
      data: { relmedId },
    });
  };

  // initializeRegistration function removed as it's no longer needed.
  // Validation happens in ParticipantRegister.js via API call now.

  const completeRegistration = async (newUserId, account) => {
    if (!newUserId) {
      console.error(
        "Cannot complete registration: missing new user ID (B2C OID)."
      );
      return false;
    }

    const b2cAuthToken = await getToken(); // Get the current B2C auth token

    if (!b2cAuthToken) {
      console.error(
        "Cannot complete registration: failed to get B2C authentication token."
      );
      return false;
    }

    // Check if this is a QR enrollment flow
    const qrEnrollmentData = localStorage.getItem("qr_enrollment_data");
    const qrUserId = localStorage.getItem("qr_user_id");

    if (qrEnrollmentData && qrUserId) {
      // QR enrollment flow - call complete-enrollment endpoint
      console.log("Completing QR enrollment flow");

      const enrollmentData = {
        user_id: qrUserId,
        b2c_id: newUserId,
      };

      return postData({
        endpoint: "complete-enrollment",
        accessToken: b2cAuthToken,
        data: enrollmentData,
      })
        .then((response) => {
          console.log("QR enrollment completion successful.");
          return true;
        })
        .catch((error) => {
          console.error("QR enrollment completion failed:", error);

          // Handle 409 (already enrolled) as success
          // This happens when user logs in after consent but is already enrolled
          if (error.status === 409) {
            console.log(
              "User already enrolled in this study - proceeding to dashboard"
            );
            return true; // Treat as success so they can access their dashboard
          }

          return false;
        });
    }

    // Legacy B2C signup-complete branch retired 2026-06-27 alongside the
    // delete of azure_functions/src/functions/b2c_signup_complete.js.
    // The Zitadel-era invite flow does its work in
    // start-invite-redemption / complete-invite-redemption — there's no
    // post-redirect "complete the signup" step to mirror. Anything left
    // landing here means stale localStorage from the B2C era; clean up
    // and bail.
    localStorage.removeItem("registration_data");
    localStorage.removeItem("invite_token");
    return false;
  };

  // Validate participant invitation token (added function)
  const validateInviteToken = async (token) => {
    if (!token) {
      throw new Error("No token provided for validation.");
    }
    // Use postData utility - assumes backend function is mapped to 'validate_invite_token' endpoint
    // No access token needed as the backend function has authLevel: 'anonymous'
    return postData({
      endpoint: "validate_invite_token",
      data: { token }, // Send token in the request body
      // No accessToken needed here
    });
    // The postData utility will handle the fetch and error checking
    // It will return the jsonBody on success or throw an error on failure
  };

  // Create invitation from expression of interest
  const createInviteFromExpression = async (
    expressionId,
    relmedId,
    displayName
  ) => {
    const token = await getToken();
    return postData({
      timeout: 30000,
      endpoint: "create_invite_from_expression",
      accessToken: token,
      data: {
        expressionId,
        relmedId,
        displayName,
      },
    });
  };

  // Get rewards data (balance and history) for a specific user or the current user
  const getRewardsBalance = async (targetUserId = null) => {
    const userIdToFetch = targetUserId || currentUser?.userId; // Use target ID or fallback to current user

    if (!userIdToFetch) {
      console.error(
        "Cannot get rewards: No user ID specified or user not authenticated."
      );
      // Return a default structure or throw error? Returning default for now.
      return { balance: 0, redeemedVouchers: [] };
    }
    const token = await getToken();
    if (!token) {
      console.error("Cannot get rewards: failed to get token.");
      return { balance: 0, redeemedVouchers: [] };
    }
    try {
      // Use the route: user/{userId}/rewards
      const response = await getData({
        endpoint: `user/${userIdToFetch}/rewards`, // Use the determined user ID
        accessToken: token,
      });
      // Return the full rewards object or a default if null/undefined
      return response || { balance: 0, redeemedVouchers: [] };
    } catch (error) {
      console.error(`Error fetching rewards for user ${userIdToFetch}:`, error);
      // Handle specific errors like 404 if needed, otherwise return default
      if (error.code === 404 || error.status === 404) {
        // 404 likely means no rewards doc exists yet, return default
        return { balance: 0, redeemedVouchers: [] };
      }
      // For other errors, maybe rethrow or return default? Returning default.
      return { balance: 0, redeemedVouchers: [] };
    }
  };

  // Redeem rewards for the current user
  const redeemRewards = async () => {
    if (!currentUser?.userId) {
      console.error("Cannot redeem rewards: user not authenticated.");
      throw new Error("User not authenticated."); // Throw error to be caught by caller
    }
    const token = await getToken();
    if (!token) {
      console.error("Cannot redeem rewards: failed to get token.");
      throw new Error("Failed to get authentication token.");
    }
    try {
      // Call the POST endpoint for redeeming rewards
      // The backend will read the current balance and call the payment API
      const response = await postData({
        // Love2Shop takes 15–30 s per order, past the 30 s default.
        timeout: 90000,
        endpoint: `redeem-rewards`, // Assuming this is the correct endpoint name
        accessToken: token,
        data: {}, // No specific data needed in body, backend reads balance
      });
      // Return the response which should contain success message and voucher details
      return response;
    } catch (error) {
      console.error("Error redeeming rewards:", error);
      // Rethrow the error so the calling component can handle it (e.g., show message)
      throw error;
    }
  };

  // Adjust reward balance for a specific user (Admin/PI only).
  // source: "manual_adjustment" (default) or "admin_override" — tags the
  // resulting rewardLog entry so the admin Reward log can distinguish
  // free-form corrections from task-complete overrides.
  const adjustRewardBalance = async (
    targetUserId,
    adjustmentAmountPence,
    reason,
    source = "manual_adjustment"
  ) => {
    if (!targetUserId || typeof adjustmentAmountPence !== "number" || !reason) {
      console.error("Invalid parameters for adjustRewardBalance");
      throw new Error("Invalid parameters provided for reward adjustment.");
    }
    const token = await getToken(); // Assumes the calling user (Admin/PI) is authenticated
    if (!token) {
      console.error("Cannot adjust rewards: failed to get admin token.");
      throw new Error("Failed to get authentication token.");
    }
    try {
      // Call the POST endpoint: user/{targetUserId}/adjust-reward
      const response = await postData({
        endpoint: `user/${targetUserId}/adjust-reward`,
        accessToken: token,
        data: {
          adjustmentAmount: adjustmentAmountPence, // Send amount in pence
          reason: reason,
          source,
        },
      });
      return response; // Return the response (e.g., { message: '...', newBalance: ... })
    } catch (error) {
      console.error(`Error adjusting rewards for user ${targetUserId}:`, error);
      throw error; // Rethrow error for the component to handle
    }
  };

  // Add a bonus reward for a specific task/session
  const addBonusReward = async (sessionCode, taskParam, bonusAmountPence) => {
    if (!currentUser?.userId) {
      console.error("Cannot add bonus reward: user not authenticated.");
      // Don't throw error here, just log and return to avoid interrupting task
      return;
    }
    if (
      !sessionCode ||
      !taskParam ||
      !bonusAmountPence ||
      bonusAmountPence <= 0
    ) {
      console.error("Invalid parameters for addBonusReward", {
        sessionCode,
        taskParam,
        bonusAmountPence,
      });
      return; // Log and exit gracefully
    }
    const token = await getToken();
    if (!token) {
      console.error("Cannot add bonus reward: failed to get token.");
      return;
    }
    try {
      // Call the POST endpoint. Pass participantId + retries so a failed
      // POST is cached in localStorage and replayed on next login by
      // processFailedUploads — same durability path setTaskState uses.
      // Backend (add-reward-bonus.js) is idempotent on (sessionCode, taskParam),
      // so replay is safe.
      await postData({
        endpoint: `add-reward-bonus`,
        accessToken: token,
        participantId: currentUser?.userId,
        retries: 3,
        data: {
          sessionCode: sessionCode,
          taskParam: taskParam,
          bonusAmount: bonusAmountPence,
        },
      });
      // Success is handled by the backend (even if already awarded)
      console.log(`Bonus reward reported for ${sessionCode}:${taskParam}`);
    } catch (error) {
      // Log error but don't throw, to avoid interrupting the task iframe
      console.error(
        `Error reporting bonus reward for ${sessionCode}:${taskParam}:`,
        error
      );
    }
  };

  // Get all PO references (PI only)
  const getPOReferences = async () => {
    const token = await getToken();
    return getData({
      endpoint: "purchase-order-references",
      accessToken: token,
    });
  };

  // Get current active PO reference (PI only)
  const getCurrentPOReference = async () => {
    const token = await getToken();
    return getData({
      endpoint: "purchase-order-references/current",
      accessToken: token,
    });
  };

  // Create new PO reference (PI only)
  const createPOReference = async (purchaseOrderReference, description) => {
    const token = await getToken();
    return postData({
      endpoint: "purchase-order-references",
      accessToken: token,
      data: {
        purchaseOrderReference,
        description,
      },
    });
  };

  // Update current PO reference (PI only)
  const updatePOReference = async (purchaseOrderReference, description) => {
    const token = await getToken();
    return postData({
      endpoint: "purchase-order-references",
      accessToken: token,
      data: {
        purchaseOrderReference,
        description,
      },
    });
  };

  // Get voucher analytics (Admin/PI only)
  const getVoucherAnalytics = async () => {
    const token = await getToken();
    return getData({
      endpoint: "voucher-analytics",
      accessToken: token,
    });
  };

  const getStudies = async (studyId) => {
    const token = await getToken();
    return getData({
      endpoint: studyId ? `studies?study_id=${encodeURIComponent(studyId)}` : "studies",
      accessToken: token,
    });
  };

  const createStudy = async (studyData) => {
    const token = await getToken();
    return postData({
      endpoint: "studies",
      data: studyData,
      accessToken: token,
    });
  };

  const updateStudy = async (studyData) => {
    const token = await getToken();
    return patchData({
      endpoint: "studies",
      data: studyData,
      accessToken: token,
    });
  };

  const validateQRRegistration = async (qrData) => {
    // Public endpoint - no auth token required
    return postData({
      endpoint: "qr-registration",
      data: qrData,
    });
  };

  const processConsentReturn = async (consentData) => {
    // Public endpoint - no auth token required
    return postData({
      endpoint: "consent-return",
      data: consentData,
    });
  };

  const uploadPIS = async (file) => {
    const token = await getToken();

    // Create FormData for file upload
    const formData = new FormData();
    formData.append("file", file);

    // Use fetch directly for multipart/form-data
    const response = await fetch("/api/upload-pis", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Upload failed");
    }

    return response.json();
  };

  // Tasks CRUD operations
  const getTasks = async () => {
    const token = await getToken();
    return getData({
      endpoint: "tasks",
      accessToken: token,
    });
  };

  const createTask = async (taskData) => {
    const token = await getToken();
    return postData({
      endpoint: "tasks",
      accessToken: token,
      data: taskData,
    });
  };

  const updateTask = async (taskData) => {
    const token = await getToken();
    return patchData({
      endpoint: "tasks",
      accessToken: token,
      data: taskData,
    });
  };

  const deleteTask = async (taskId) => {
    const token = await getToken();
    return deleteData({
      endpoint: `tasks?id=${taskId}`,
      accessToken: token,
    });
  };

  // Email templates (EmailTemplates Cosmos container)
  const getEmailTemplates = async () => {
    const token = await getToken();
    return getData({ endpoint: "email-templates", accessToken: token });
  };

  const updateEmailTemplate = async ({ templateId, subject, body, description }) => {
    const token = await getToken();
    return patchData({
      endpoint: "email-templates",
      accessToken: token,
      data: { templateId, subject, body, description },
    });
  };

  const sendTestEmailTemplate = async ({
    templateId,
    subject,
    body,
    sampleData,
  }) => {
    const token = await getToken();
    return postData({
      endpoint: "email-templates/test",
      accessToken: token,
      data: { templateId, subject, body, sampleData },
    });
  };

  // Admin: reset a TEST-site participant's progress (server-side guard
  // rejects anything other than site === "TEST").
  const resetTestParticipant = async ({ participantId, targetSessionId }) => {
    const token = await getToken();
    return postData({
      endpoint: "reset-test-participant",
      accessToken: token,
      data: { participantId, targetSessionId },
    });
  };

  // Links CRUD operations
  const getLinks = async (studyId, pharmacy = null, drug = null) => {
    const token = await getToken();
    let endpoint = `links?study_id=${studyId}`;

    if (pharmacy && drug) {
      endpoint += `&pharmacy=${encodeURIComponent(
        pharmacy
      )}&drug=${encodeURIComponent(drug)}`;
    }

    return getData({
      endpoint,
      accessToken: token,
    });
  };

  const generateLinks = async (studyId, pharmacy, drug, count, after = 0) => {
    const token = await getToken();
    return getData({
      endpoint: `links?study_id=${studyId}&pharmacy=${encodeURIComponent(
        pharmacy
      )}&drug=${encodeURIComponent(
        drug
      )}&count=${count}&after=${after}&createIfMissing=true`,
      accessToken: token,
    });
  };

  const createLink = async (linkData) => {
    const token = await getToken();
    return postData({
      endpoint: "links",
      accessToken: token,
      data: linkData,
    });
  };

  const deleteLink = async (hash) => {
    const token = await getToken();
    return deleteData({
      endpoint: `links?hash=${hash}`,
      accessToken: token,
    });
  };

  // Resolve hash to study data (public)
  const resolveLink = async (hash) => {
    return getData({
      endpoint: `links?hash=${hash}`,
    });
  };

  // Participant data (for logged-in participant)
  const getParticipantData = async (studyId = null) => {
    const token = await getToken();
    const endpoint = studyId
      ? `participant?study_id=${studyId}`
      : "participant";
    return getData({
      endpoint,
      accessToken: token,
    });
  };

  const setParticipantState = async (participantData) => {
    const token = await getToken();
    // Cache key for failed-write replay (v1.0.17 durability path). Prefer
    // the participant doc's composite id; fall back to the current user's
    // Zitadel sub if the caller hasn't loaded participant data yet.
    const cacheKey = participantData?.id || currentUser?.userId;
    return patchData({
      endpoint: "participant",
      accessToken: token,
      participantId: cacheKey,
      retries: 3,
      data: participantData,
    });
  };

  // Get all enrollments for current user
  const getParticipantEnrollments = async () => {
    const token = await getToken();
    return getData({
      endpoint: "participant-enrollments",
      accessToken: token,
    });
  };

  // Direct enrollment for existing users
  const enrollExistingUser = async (enrollmentData) => {
    const token = await getToken();
    return postData({
      endpoint: "enroll-existing-user",
      accessToken: token,
      data: enrollmentData,
    });
  };

  // Set task or session state (rapid atomic updates)
  // Passes participantId + retries so failed state writes get cached in
  // localStorage (via cacheFailedUpload) and retried on next login by
  // processFailedUploads — the same durability path trial data has.
  const setTaskState = async (
    session_id,
    state,
    task = null,
    study_id = null,
    participant_id = null
  ) => {
    const token = await getToken();
    return postData({
      endpoint: "task-state",
      accessToken: token,
      participantId: participant_id || currentUser?.userId,
      retries: 3,
      data: { session_id, task, state, study_id, participant_id },
    });
  };

  // Set participant-level state (persists across all sessions)
  const setParticipantTaskState = async (
    state,
    study_id = null,
    participant_id = null
  ) => {
    const token = await getToken();
    return postData({
      endpoint: "task-state",
      accessToken: token,
      participantId: participant_id || currentUser?.userId,
      retries: 3,
      data: { type: "participant", state, study_id, participant_id },
    });
  };

  // Email history for a participant — everything sent, queued, or failed.
  // Backs the Emails tab in the admin participant dialog so staff can
  // confirm what a participant was actually sent when they report not
  // having received something.
  const getParticipantEmails = async (targetUserId) => {
    if (!targetUserId) {
      throw new Error("getParticipantEmails requires a user ID");
    }
    const token = await getToken();
    return getData({
      endpoint: `user/${targetUserId}/emails`,
      accessToken: token,
    });
  };

  // Stop a queued email before it goes out. Staff-only server-side; the
  // Cancel control in the email history is hidden for anything that is not
  // still pending, but the API re-checks rather than trusting the UI.
  const cancelScheduledEmail = async (targetUserId, emailId) => {
    if (!targetUserId || !emailId) {
      throw new Error("cancelScheduledEmail requires a user ID and an email ID");
    }
    const token = await getToken();
    return postData({
      endpoint: `user/${encodeURIComponent(targetUserId)}/emails/${encodeURIComponent(emailId)}/cancel`,
      accessToken: token,
      data: {},
    });
  };

  // Task assignments (for logged-in participant)
  const getTaskAssignments = async (participantId) => {
    const token = await getToken();
    return getData({
      endpoint: `task-assignments?participant_id=${participantId}`,
      accessToken: token,
    });
  };

  return (
    <DataProviderContext.Provider
      value={{
        currentUser,
        getUserState,
        setUserState,
        setParticipantConfig,
        getUsersStatuses,
        getParticipants,
        getParticipantById,
        createInvite,
        checkRelmedId,
        resendInvite,
        postTaskData,
        getSubmissionMetrics,
        retrySubmission,
        isAuthenticated,
        // initializeRegistration removed
        completeRegistration,
        validateInviteToken, // Expose the new function
        getRewardsBalance,
        getParticipantEmails,
        cancelScheduledEmail,
        redeemRewards,
        adjustRewardBalance,
        addBonusReward, // Add addBonusReward to context value
        getPOReferences,
        getCurrentPOReference,
        createPOReference,
        updatePOReference,
        getVoucherAnalytics,
        getStudies,
        createStudy,
        updateStudy,
        validateQRRegistration,
        processConsentReturn,
        uploadPIS,
        getTasks,
        createTask,
        updateTask,
        deleteTask,
        getEmailTemplates,
        updateEmailTemplate,
        sendTestEmailTemplate,
        resetTestParticipant,
        getLinks,
        generateLinks,
        createLink,
        deleteLink,
        resolveLink,
        getParticipantData,
        setParticipantState,
        setTaskState,
        setParticipantTaskState,
        getTaskAssignments,
        getParticipantEnrollments,
        enrollExistingUser,
      }}
    >
      {children}
    </DataProviderContext.Provider>
  );
}
